import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { disableDevicePush } from "../lib/devicePush";
import { api, getToken, setToken, clearToken, friendlyApiMessage } from "../api";
import {
  clearCached,
  clearCachedScope,
  getCacheScope,
  setCacheScope,
} from "../lib/dataCache";

const AuthContext = createContext(null);
export const ORDER_AUTH_RETURN_KEY = "arctic_order_auth_return_path";

const cacheScopeFor = (user) => {
  if (user && user.id) return `user:${user.id}:role:${user.role || "unknown"}`;
  if (user === false) return "auth:guest";
  return "auth:unresolved";
};

function activateCacheScope(user, { clearAll = false } = {}) {
  const nextScope = cacheScopeFor(user);
  if (clearAll) clearCached();
  else if (getCacheScope() !== nextScope) clearCachedScope();
  setCacheScope(nextScope);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);   // null = unknown, false = anonymous, object = logged-in
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const startingToken = getToken();
    if (!startingToken) {
      activateCacheScope(false, { clearAll: true });
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      if (getToken() !== startingToken) {
        setLoading(false);
        return;
      }
      activateCacheScope(data);
      setUser(data);

      // /auth/me is the authoritative identity read. Do not keep the entire
      // app behind the global loading gate while the secondary token refresh
      // completes. Refresh the token in the background after the UI is usable.
      setLoading(false);
      if (!data.must_reset_password) {
        void api.post("/auth/refresh")
          .then(({ data: refreshed }) => {
            if (refreshed?.token && getToken() === startingToken) {
              setToken(refreshed.token);
            }
          })
          .catch((refreshErr) => {
            if (process.env.NODE_ENV !== "production") {
              console.warn("[auth refresh] skipped:", refreshErr);
            }
          });
      }
    } catch {
      if (getToken() === startingToken) {
        clearToken();
        activateCacheScope(false, { clearAll: true });
        setUser(false);
      }
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Ordering requires an authenticated customer account. AuthProvider sits
  // above BrowserRouter, so preserve the intended destination in session
  // storage before sending an anonymous cart visitor to the login screen.
  useEffect(() => {
    if (loading || user !== false || typeof window === "undefined") return;
    const path = (window.location?.pathname || "").replace(/\/+$/, "") || "/";
    if (path !== "/cart") return;
    try {
      window.sessionStorage.setItem(ORDER_AUTH_RETURN_KEY, "/cart");
    } catch {
      // Private browsing may deny storage; login still works without return-path convenience.
    }
    window.location.replace("/login?account_required=1");
  }, [loading, user]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setToken(data.token);
      activateCacheScope(data.user, { clearAll: true });
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      const raw = e?.response?.data?.detail;
      const detail = (raw && typeof raw === "object") ? raw : null;
      return {
        ok: false,
        error: friendlyApiMessage(e, "We can’t sign you in right now. Please try again."),
        detail,
      };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      setToken(data.token);
      activateCacheScope(data.user, { clearAll: true });
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: friendlyApiMessage(e, "Couldn’t create your account. Please try again.") };
    }
  };

  const logout = useCallback(() => {
    const endingToken = getToken();
    void disableDevicePush(endingToken).catch(() => {});
    clearToken();
    activateCacheScope(false, { clearAll: true });
    try { localStorage.removeItem("arctic_last_guest_order"); }
    catch { /* private mode ok */ }
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, refresh }),
    [user, loading, logout, refresh],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
